import { useEffect, useLayoutEffect, useState } from 'react'
import { createPortal } from 'react-dom'

import Action from 'ageas-react-component-action'
import MainLoader from 'ageas-react-component-loader'
import Summary from 'ageas-react-component-summary'

import moment from 'moment'
import { useNavigate } from 'react-router-dom'
import { Collapse } from '../../Components/Collapse'
import { Dialog } from '../../Components/Dialog'
import { QuoteRequestStatus } from '../../Components/QuoteRequestStatus'
import { Approved } from '../../Components/QuoteRequestStatus/Contents/Approved'
import { TypeProps } from '../../Components/QuoteRequestStatus/Status'
import { ReportSimulationModal } from '../../Components/ReportSimulationModal/ReportSimulationModal'
import { ReportAdditionalDocumentsRequired } from '../../Components/SummaryConditions/Modals/ReportAdditionalDocumentsRequired'
import { RequestAuthorization } from '../../Components/SummaryConditions/Modals/RequestAuthorization'
import { TableInsured } from '../../Components/SummaryConditions/TableInsured'
import { MainContainer } from '../../GlobalStyles'
import { useAnimalStore } from '../../Stores/animalStore'
import { usePaymentStore } from '../../Stores/paymentStore'
import { usePolicyholderDetailsStore } from '../../Stores/policyholderDetailsStore'
import { useProgressStore } from '../../Stores/progressStore'
import { useReportStore } from '../../Stores/reportStore'
import { useSimulatorStore } from '../../Stores/simulatorStore'
import { useGetQuotationStore } from '../../Stores/useGetQuotationStore'
import { useRequestQuotationStore } from '../../Stores/useRequestQuotationStore'
import { useResumeStore } from '../../Stores/useResumeStore'
import { useRunPetisSimulationStore } from '../../Stores/useRunPetisSimulationStore'
import { useSimulationSummaryDownloadStore } from '../../Stores/useSimulationSummaryDownloadStore'
import { useWarningStore } from '../../Stores/warningStore'
import { PRICE_TYPE } from '../../constants/priceType'
import {
  QUOTATION_STATUS,
  QUOTATION_STATUS_CODE,
} from '../../constants/quotationStatusCode'
import { ROUTES } from '../../constants/routes'
import { WARNING_BAR } from '../../constants/warningBar'
import { useAcceptConditions } from '../../hooks/api/acceptConditions/useAcceptConditions'
import { useAcceptConditionsPayload } from '../../hooks/api/acceptConditions/useAcceptConditionsPayload'
import { useGenerateProposalDocuments } from '../../hooks/api/generateProposalDocuments/useGenerateProposalDocuments'
import { useGenerateProposalDocumentsPayload } from '../../hooks/api/generateProposalDocuments/useGenerateProposalDocumentsPayload'
import { useGetQuotation } from '../../hooks/api/getQuotation/useGetQuotation'
import { useGetQuotationPayload } from '../../hooks/api/getQuotation/useGetQuotationPayload'
import { useIssueCost } from '../../hooks/useIssueCost'
import { t } from '../../i18n'
import { currencyFormatter } from '../../utils/currencyFormatter'
import { TableContainer } from '../AdditionalRiskData/style'
import { CollapseContainer, Container } from './styles'

const elementPortal = document.getElementsByTagName('body')?.[0]

type GetQuotationStatus = {
  status: TypeProps
  message: string
}

const currentDate = moment()

export const SummaryConditions = () => {
  const navigate = useNavigate()
  const [showReportSimulation, setShowReportSimulation] = useState(false)
  const { getIssueCostValue } = useIssueCost()
  const [showDialogAcceptConditions, setShowDialogAcceptConditions] =
    useState(false)
  const [showRequestAuth, setShowRequestAuth] = useState(false)
  const { directDebit, crossSell } = usePaymentStore()
  const { setCurrentStep } = useProgressStore()
  const {
    totalPricesObjects,
    typeOfPrice,
    initialDate,
    isAcceptedConditions,
    setIsAcceptedConditions,
    hasGenerateProposal,
    setHasGenerateProposal,
  } = useSimulatorStore()
  const { policyholderDetailsSelected } = usePolicyholderDetailsStore()
  const { animalsList } = useAnimalStore()
  const { setWarningOptions } = useWarningStore()
  const { setShowCompleteReport } = useSimulationSummaryDownloadStore()
  const {
    isReferralWarning,
    setIsReferralWarning,
    setIsReferral,
    selectedPackCampaign,
  } = useRunPetisSimulationStore()
  const { setReportGenerated, setIsReportGeneratedEnabled } = useReportStore()
  const { generateProposalPayload } = useGenerateProposalDocumentsPayload()
  const { acceptConditionsPayload } = useAcceptConditionsPayload()
  const { getQuotation } = useGetQuotationStore()
  const { requestQuotation } = useRequestQuotationStore()
  const { isQuotationResume, disabledStepsByResume } = useResumeStore()
  const payloadGetQuotation = useGetQuotationPayload()

  const {
    mutate: mutateProposal,
    isLoading: isLoadingProposal,
    isSuccess: isSuccessProposal,
  } = useGenerateProposalDocuments()

  const { mutate: mutateGetQuotation, isLoading: isLoadingGetQuotation } =
    useGetQuotation()

  const {
    isSuccess: isSuccessAcceptCondition,
    isLoading,
    mutate,
  } = useAcceptConditions()

  const loading = isLoading || isLoadingProposal || isLoadingGetQuotation

  const handleAcceptConditionsClick = () => {
    mutate(acceptConditionsPayload)
  }

  const totalPriceResult = () => {
    let total = 0

    for (const key in totalPricesObjects) {
      if (typeof totalPricesObjects[key] === 'number') {
        total += totalPricesObjects[key]
      }
    }

    return total
  }

  function getQuotationStatus(code: string): GetQuotationStatus {
    let status: TypeProps
    let message: string

    switch (code) {
      case QUOTATION_STATUS_CODE.AC:
        status = 'approved'
        message = QUOTATION_STATUS.AC
        break
      case QUOTATION_STATUS_CODE.RJ:
        status = 'refused'
        message = QUOTATION_STATUS.RJ
        break
      case QUOTATION_STATUS_CODE.MI:
        status = 'info'
        message = QUOTATION_STATUS.MI
        break
      case QUOTATION_STATUS_CODE.EX:
        status = 'expired'
        message = QUOTATION_STATUS.EX
        break
      default:
        status = 'pending'
        message = QUOTATION_STATUS.AN
        break
    }

    return { status, message }
  }

  const hasAnimalBetweenEightAndTen =
    animalsList.filter((animal) => {
      const yearDifference = currentDate.diff(animal.birthDate, 'years')
      if (yearDifference >= 8 && yearDifference <= 10) {
        return animal
      } else {
        return null
      }
    }).length > 0

  useEffect(() => {
    if (isSuccessProposal) {
      setReportGenerated(true)
      setIsReportGeneratedEnabled(false)
      setShowReportSimulation(true)
      setShowCompleteReport(true)
      setTimeout(() => {
        setHasGenerateProposal(true)
      }, 1000)
    }
  }, [isSuccessProposal, setShowCompleteReport])

  useEffect(() => {
    if (getQuotation?.policyQuotation?.status === QUOTATION_STATUS_CODE.AC) {
      setIsReferralWarning(false)
      setIsReferral(false)
      setWarningOptions({
        labelTypes: [],
        mode: WARNING_BAR.mode.hide,
        modal: false,
      })
    }
  }, [getQuotation?.policyQuotation?.status])

  useEffect(() => {
    if (isSuccessAcceptCondition) {
      setIsAcceptedConditions(true)
      setShowDialogAcceptConditions(false)
      navigate(ROUTES.formalization)
    }
  }, [isSuccessAcceptCondition])

  useEffect(() => {
    if (Boolean(requestQuotation?.quotationId) && !isQuotationResume) {
      mutateGetQuotation(payloadGetQuotation)
    }
  }, [isQuotationResume, requestQuotation?.quotationId])

  useLayoutEffect(() => {
    setCurrentStep(5)
  }, [setCurrentStep])

  return (
    <MainContainer>
      <MainLoader show={loading} />
      <Container>
        <Summary
          id="summaryPrice"
          readOnly
          newValue={currencyFormatter(totalPriceResult())}
          label={false}
          labelGrey={t('pages.step2.footerbar.toolTip', {
            value: getIssueCostValue() || '',
          })}
          periodCaption={PRICE_TYPE[typeOfPrice as never]}
          colorTheme="blue"
          unit="€"
          version="2"
        />
        <div className="grid-container">
          <div className="grid align-right">
            {/* <div className="list-item">
              <span className="title">{t('pages.step5.crossSell')}</span>
              <span className="value">
                {crossSell ? t('general.pt.yes') : t('general.pt.no')}
              </span>
            </div> */}
            <div className="list-item">
              <span className="title">{t('pages.step5.campaign')} </span>
              <span className="value">
                {(selectedPackCampaign?.id || '')?.length > 0
                  ? selectedPackCampaign?.additionalText || ''
                  : t('general.pt.no')}
              </span>
            </div>
          </div>
          <div className="grid">
            <div className="list-item">
              <span className="title">{t('pages.step5.directDebit')}</span>
              <span className="value">
                {Boolean(Number(directDebit))
                  ? t('general.pt.yes')
                  : t('general.pt.no')}
              </span>
            </div>
          </div>
        </div>
        <CollapseContainer>
          <Collapse title={t('pages.step5.generalData')} open>
            <div className="grid-container">
              <div className="grid">
                <div className="list-item">
                  <span className="title">{t('pages.step5.initialDate')}</span>
                  <span className="value">{initialDate}</span>
                </div>
                <div className="list-item">
                  <span className="title">
                    {t('pages.step5.quantityAnimals')}
                  </span>
                  <span className="value">{animalsList?.length}</span>
                </div>
              </div>
              <div className="grid">
                <div className="list-item">
                  <span className="title">{t('pages.step5.NIF')}</span>
                  <span className="value">
                    {policyholderDetailsSelected?.vatNumber}
                  </span>
                </div>
                <div className="list-item">
                  <span className="title">
                    {t('pages.step5.policyholderName')}
                  </span>
                  <span className="value">
                    {policyholderDetailsSelected?.name}
                  </span>
                </div>
              </div>
            </div>
          </Collapse>
          <Collapse title={t('pages.step5.riskDataAndCover')}>
            <TableContainer>
              <TableInsured />
            </TableContainer>
          </Collapse>
        </CollapseContainer>
        {Boolean(getQuotation?.policyQuotation?.quotationId) && (
          <QuoteRequestStatus
            type={
              getQuotationStatus(getQuotation?.policyQuotation?.status || '')
                .status
            }
            typeMessage={
              getQuotationStatus(getQuotation?.policyQuotation?.status || '')
                .message
            }
          >
            <Approved />
          </QuoteRequestStatus>
        )}
        <div className="button-group">
          {!Boolean(requestQuotation?.quotationId) &&
            !hasGenerateProposal &&
            !hasAnimalBetweenEightAndTen &&
            !disabledStepsByResume.step5 && (
              <Action
                id="sendForQuotation"
                label={t('pages.step5.sendForQuotationBtn')}
                type="button"
                buttonType="secondary"
                onValueChanged={() => setShowRequestAuth(true)}
              />
            )}
          {!hasGenerateProposal && !disabledStepsByResume.step5 && (
            <Action
              id="buttonReportGenerator"
              label={t('pages.step5.reportGeneratorBtn')}
              type="button"
              disable={
                loading ||
                (getQuotation?.policyQuotation?.status !==
                  QUOTATION_STATUS_CODE.AC &&
                  getQuotation?.policyQuotation?.status !== undefined) ||
                isReferralWarning
              }
              onValueChanged={() => {
                mutateProposal(generateProposalPayload)
              }}
            />
          )}
          {!isSuccessAcceptCondition &&
            hasGenerateProposal &&
            !isAcceptedConditions &&
            !disabledStepsByResume.step5 && (
              <Action
                id="buttonAcceptConditions"
                label={t('pages.step5.acceptConditionsBtn')}
                type="button"
                disable={loading}
                onValueChanged={() => setShowDialogAcceptConditions(true)}
              />
            )}
          {(hasGenerateProposal && isAcceptedConditions) ||
            (disabledStepsByResume.step5 && (
              <Action
                id="buttonContinue"
                label={t('general.continue')}
                type="button"
                disable={loading || disabledStepsByResume.step5}
                onValueChanged={() => navigate(ROUTES.formalization)}
              />
            ))}
        </div>
        {showDialogAcceptConditions && (
          <Dialog>
            <ReportAdditionalDocumentsRequired
              disabled={loading}
              onCancel={() => {
                setShowDialogAcceptConditions(false)
              }}
              onClick={handleAcceptConditionsClick}
            />
          </Dialog>
        )}
        {createPortal(
          <RequestAuthorization
            isVisible={showRequestAuth}
            closeAction={() => setShowRequestAuth(false)}
          />,
          elementPortal!,
        )}
        {createPortal(
          <>
            <ReportSimulationModal
              isVisible={showReportSimulation}
              closeAction={() => {
                setShowReportSimulation(false)
                setShowDialogAcceptConditions(true)
              }}
            />
          </>,
          elementPortal!,
        )}
      </Container>
    </MainContainer>
  )
}
